/**
 * Silent session renewal — keep a signed-in user signed in, with no page reload.
 *
 * ## Why this has to be PROACTIVE, not reactive
 *
 * The obvious design is "on 401, refresh and retry". That alone cannot work
 * here, and it is worth recording why so nobody simplifies it back.
 *
 * IAM's `auth/refresh-token/` authenticates the refresh call itself with the
 * CURRENT access token. Verified against dev IAM:
 *
 *   - no Authorization header  -> 401 "Authentication credentials were not
 *     provided." (rejected before the body is even considered)
 *   - expired access token     -> 401 "Token is invalid" / code
 *     "user_not_found" (it parsed the token and rejected the identity — the
 *     refresh token in the body was never reached)
 *
 * So once the access token has expired, the refresh token is useless. By the
 * time a 401 arrives, it is already too late to recover.
 *
 * The renewal therefore has to happen while the access token is STILL VALID:
 * a timer that fires shortly before `exp` and swaps the token underneath the
 * running app. The 401 path is kept only as a backstop for the case where a
 * refresh is genuinely still possible (clock skew, a token whose expiry we
 * could not read) — it is not the primary mechanism.
 *
 * ## Why a timer rather than a check-before-each-request
 *
 * A pre-request check would have to wrap all 56 fetch call sites, and would
 * still miss the idle case: someone leaves the schedule-interview form open
 * for an hour, issues no request, and their token dies with nothing to notice.
 * One timer keyed off `exp` covers both, and a `visibilitychange` catch-up
 * covers the laptop-was-asleep case, where timers do not fire on schedule.
 */
import { readAccessToken, readRefreshToken } from './auth-token-store';
import { getJwtExpiry } from './auth-session';
/**
 * Renew this long before the token actually expires. Generous enough to absorb
 * a slow network and modest client-clock skew, so the refresh request is sent
 * while the backend still considers the access token valid.
 */
const RENEW_LEAD_SEC = 120;
/** Never schedule a timer tighter than this — avoids a hot retry loop. */
const MIN_DELAY_MS = 1000;
/**
 * setTimeout is not reliable beyond ~24.8 days (int32 overflow) and a very long
 * sleep is better re-evaluated periodically anyway. Re-arm at most this far out.
 */
const MAX_DELAY_MS = 10 * 60 * 1000;
let timer = null;
let installed = false;
let refreshFn = null;
/** In-flight refresh, shared so N concurrent callers make ONE network call. */
let inFlight = null;
/**
 * Refresh the access token, collapsing concurrent callers onto one request.
 *
 * Without this, a dashboard that fires six requests at once would send six
 * refreshes; five would present an access token that the first refresh had
 * already rotated away, and would fail.
 */
export const refreshNow = () => {
    if (inFlight)
        return inFlight;
    if (!refreshFn)
        return Promise.resolve(null);
    inFlight = refreshFn()
        .catch(() => null)
        .finally(() => {
        inFlight = null;
    });
    return inFlight;
};
/** Clear any pending renewal. */
export const cancelScheduledRefresh = () => {
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
};
export const scheduleTokenRefresh = () => {
    cancelScheduledRefresh();
    if (typeof window === 'undefined')
        return;
    const token = readAccessToken();
    if (!token || !readRefreshToken())
        return;
    const exp = getJwtExpiry(token);
    if (exp === null)
        return;
    const msUntilRenew = exp * 1000 - RENEW_LEAD_SEC * 1000 - Date.now();
    const delay = Math.max(MIN_DELAY_MS, Math.min(msUntilRenew, MAX_DELAY_MS));
    timer = setTimeout(() => {
        timer = null;
        if (msUntilRenew > MAX_DELAY_MS) {
            scheduleTokenRefresh();
            return;
        }
        refreshNow().then((next) => {
            if (next)
                scheduleTokenRefresh();
        });
    }, delay);
};
export const installSilentRefresh = (refresh) => {
    if (typeof window === 'undefined')
        return;
    refreshFn = refresh;
    if (installed) {
        scheduleTokenRefresh();
        return;
    }
    installed = true;
    window.addEventListener('authChange', scheduleTokenRefresh);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible')
            scheduleTokenRefresh();
    });
    scheduleTokenRefresh();
};
/** Reset module state. Tests only. */
export const __resetSilentRefresh = () => {
    cancelScheduledRefresh();
    installed = false;
    refreshFn = null;
    inFlight = null;
};
